import React from 'react';

/**
 * ShareSurveyModal — send a copy of a survey to another account.
 *
 * Two fields: recipient account email (required) and an optional note.
 * Styled to match TextInputModal/ConfirmModal. The share carries a frozen
 * snapshot server-side; nothing is downloaded or emailed from the browser.
 *
 * @param {boolean} open        - When false, nothing renders.
 * @param {string}  surveyName  - Shown in the title for context.
 * @param {Function} onSubmit   - Called with ({ toEmail, message }).
 * @param {Function} onCancel   - Called on Cancel, Escape, or backdrop click.
 */
export default function ShareSurveyModal({ open, surveyName, onSubmit, onCancel }) {
    const [email, setEmail] = React.useState('');
    const [message, setMessage] = React.useState('');
    const emailRef = React.useRef(null);

    React.useEffect(() => {
        if (!open) return;
        setEmail('');
        setMessage('');
        const t = setTimeout(() => emailRef.current?.focus(), 0);
        return () => clearTimeout(t);
    }, [open]);

    React.useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onCancel]);

    if (!open) return null;

    const trimmedEmail = email.trim();
    const valid = trimmedEmail.includes('@');
    const submit = () => { if (valid) onSubmit?.({ toEmail: trimmedEmail, message: message.trim() }); };

    const labelStyle = { display: 'block', margin: '0 0 6px', fontSize: 13, color: '#3C4043', fontWeight: 500 };
    const inputStyle = {
        width: '100%', boxSizing: 'border-box', padding: '8px 12px',
        border: '1px solid #DADCE0', borderRadius: 4, fontSize: 14,
        color: '#3C4043', outline: 'none', marginBottom: 16,
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onCancel}>
            <div style={{
                background: '#fff', borderRadius: 8, padding: 24, maxWidth: 440, width: '90%',
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }} onClick={e => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#3C4043' }}>
                    Share “{surveyName}”
                </h3>
                <div style={{ margin: '0 0 16px', fontSize: 14, color: '#5F6368', lineHeight: 1.5 }}>
                    Sends a copy of this survey to another account. The recipient
                    reviews and accepts it into their own workspace; your original
                    is unaffected.
                </div>
                <label style={labelStyle}>Recipient account email</label>
                <input
                    ref={emailRef}
                    type="email"
                    value={email}
                    placeholder="author@example.com"
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
                    style={inputStyle}
                />
                <label style={labelStyle}>Note (optional)</label>
                <textarea
                    value={message}
                    placeholder="Why you're sharing this survey…"
                    onChange={e => setMessage(e.target.value)}
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical', marginBottom: 20, fontFamily: 'inherit' }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onCancel} style={{
                        padding: '6px 16px', border: '1px solid #DADCE0', borderRadius: 4,
                        background: '#fff', color: '#3C4043', cursor: 'pointer', fontSize: 13,
                    }}>Cancel</button>
                    <button onClick={submit} disabled={!valid} style={{
                        padding: '6px 16px', border: 'none', borderRadius: 4,
                        background: valid ? '#1967D2' : '#A8C7FA', color: '#fff',
                        cursor: valid ? 'pointer' : 'default', fontSize: 13, fontWeight: 600,
                    }}>Share</button>
                </div>
            </div>
        </div>
    );
}
